/*
 *
 * PiecesList
 *
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { useInjectReducer } from 'utils/injectReducer';
import { createStructuredSelector } from 'reselect';
import { makeSelectListPieces } from './selectors';
import reducer from './reducer';
import { RootState } from './types';
import { makeSelectCabinet } from 'containers/Configurator/selectors/furniture';
import { Cabinet } from 'lib/modelBuilder/entities/Cabinet';
import { CabinetPieces } from 'lib/modelBuilder/Data';
import { makeSelectIsVisiblePieceList } from 'containers/Configurator/selectors/general';
import styled from 'styled-components/macro';
import PieceListItem from 'containers/PiecesList/PieceListItem';
import { useTranslation } from 'react-i18next';
import useMedia from 'use-media';
import ReactTooltip from 'react-tooltip';

interface StateProps {
  piecesListIsVisible: boolean;
  cabinetPiecesList: CabinetPieces | null;
  cabinet: Cabinet | null;
}

const mapStateToProps = createStructuredSelector<RootState, StateProps>({
  piecesListIsVisible: makeSelectIsVisiblePieceList(),
  cabinetPiecesList: makeSelectListPieces(),
  cabinet: makeSelectCabinet(),
});

function PiecesList() {
  useInjectReducer({ key: 'piecesList', reducer: reducer });
  const { t } = useTranslation();

  const { piecesListIsVisible, cabinetPiecesList, cabinet } = useSelector(
    mapStateToProps,
  );

  const isMobile = useMedia({ maxWidth: 1023 });
  if (isMobile) return null;

  if (piecesListIsVisible) {
    if (!cabinetPiecesList || !cabinet) {
      return (
        <div>
          <div className="w-100">There is no cabinet selected</div>
        </div>
      );
    } else {
      return (
        <div>
          <div className="w-100">
            <div className="flex items-center">
              <div className="px-6 py-1 text-xl">
                <span className="font-semibold">
                  {cabinet.displayPriceTaxInclude()}€
                </span>
              </div>
              <div className="px-6 py-1 text-lg">
                {cabinet.salesPrice.toFixed(2)}€
                <span className="text-sm ml-1">HTVA</span>
              </div>
              <div className="px-6 py-1 text-lg">
                {cabinet.weightKilo.toFixed(2)} kg
              </div>
            </div>
            <div className="flex">
              <div className="px-2">
                {t('short.height')}: {cabinet.height} mm
              </div>
              <div className="px-2">
                {t('short.width')}: {cabinet.width} mm
              </div>
              <div className="px-2">
                {t('short.depth')}: {cabinet.depth} mm
              </div>
            </div>

            {/*<div>*/}
            {/*  <div className="px-2">Support: {cabinet.support}</div>*/}
            {/*</div>*/}

            <div className="mt-4">
              {cabinetPiecesList.doors.length > 0 && (
                <PiecesSection className="piecesSection">
                  <PiecesSectionTitle>{t('Doors')}</PiecesSectionTitle>
                  <ul>
                    {cabinetPiecesList.doors.map(item => (
                      <li key={item.reference}>
                        <PieceListItem item={item} />
                      </li>
                    ))}
                  </ul>
                </PiecesSection>
              )}

              {cabinetPiecesList.panes.length > 0 && (
                <PiecesSection>
                  <PiecesSectionTitle>{t('Panes')}</PiecesSectionTitle>
                  <div className="">
                    <ul>
                      {cabinetPiecesList.panes.map(item => (
                        <li key={item.reference}>
                          <PieceListItem item={item} />
                        </li>
                      ))}
                    </ul>
                  </div>
                </PiecesSection>
              )}

              {cabinetPiecesList.plates.length > 0 && (
                <PiecesSection className="piecesSection">
                  <PiecesSectionTitle>{t('Plates')}</PiecesSectionTitle>
                  <ul>
                    {cabinetPiecesList.plates.map(item => (
                      <li key={item.reference}>
                        <PieceListItem item={item} />
                      </li>
                    ))}
                  </ul>
                </PiecesSection>
              )}

              {cabinetPiecesList.crossPieces.length > 0 && (
                <PiecesSection>
                  <PiecesSectionTitle>{t('Cross Pieces')}</PiecesSectionTitle>
                  <ul>
                    {cabinetPiecesList.crossPieces.map(item => (
                      <li key={item.reference}>
                        <PieceListItem item={item} />
                      </li>
                    ))}
                  </ul>
                </PiecesSection>
              )}

              {cabinetPiecesList.corners.length > 0 && (
                <PiecesSection>
                  <PiecesSectionTitle>{t('Corners')}</PiecesSectionTitle>
                  <ul>
                    {cabinetPiecesList.corners.map(item => (
                      <li key={item.reference}>
                        <PieceListItem item={item} />
                      </li>
                    ))}
                  </ul>
                </PiecesSection>
              )}

              {cabinetPiecesList.battens.length > 0 && (
                <PiecesSection>
                  <PiecesSectionTitle>{t('Battens')}</PiecesSectionTitle>
                  <ul>
                    {cabinetPiecesList.battens.map(item => (
                      <li key={item.reference}>
                        <PieceListItem item={item} />
                      </li>
                    ))}
                  </ul>
                </PiecesSection>
              )}

              {cabinetPiecesList.drawers.length > 0 && (
                <PiecesSection>
                  <PiecesSectionTitle>{t('Drawers')}</PiecesSectionTitle>
                  <ul>
                    {cabinetPiecesList.drawers.map(item => (
                      <li key={item.reference}>
                        <PieceListItem item={item} />
                      </li>
                    ))}
                  </ul>
                </PiecesSection>
              )}

              {cabinetPiecesList.intermediateShelves.length > 0 && (
                <PiecesSection>
                  <PiecesSectionTitle>
                    {t('InterShelves', 'Inter. Shelves')}
                  </PiecesSectionTitle>
                  <ul>
                    {cabinetPiecesList.intermediateShelves.map(item => (
                      <li key={item.reference}>
                        <PieceListItem item={item} />
                      </li>
                    ))}
                    {cabinetPiecesList.supportIntermediateShelves.map(item => (
                      <li key={item.reference}>
                        <PieceListItem item={item} />
                      </li>
                    ))}
                    {cabinetPiecesList?.reinforcementIntermediatesShelves.map(
                      item => (
                        <li key={item.reference}>
                          <PieceListItem item={item} />
                        </li>
                      ),
                    )}
                  </ul>
                </PiecesSection>
              )}

              {(cabinetPiecesList.doorsHandles.length > 0 ||
                cabinetPiecesList.drawersHandles.length > 0 ||
                cabinetPiecesList.drawersSlats.length > 0) && (
                <PiecesSection>
                  <PiecesSectionTitle>{t('Handles')}</PiecesSectionTitle>
                  <ul>
                    {cabinetPiecesList.doorsHandles.map(item => (
                      <li key={item.reference}>
                        <PieceListItem item={item} />
                      </li>
                    ))}
                    {cabinetPiecesList.drawersHandles.map(item => (
                      <li key={item.reference}>
                        <PieceListItem item={item} />
                      </li>
                    ))}
                    {cabinetPiecesList.drawersSlats.map(item => (
                      <li key={item.reference}>
                        <PieceListItem item={item} />
                      </li>
                    ))}
                  </ul>
                </PiecesSection>
              )}

              {cabinetPiecesList.rods.length > 0 && (
                <PiecesSection>
                  <PiecesSectionTitle>{t('Rods')}</PiecesSectionTitle>
                  <ul>
                    {cabinetPiecesList.rods.map(item => (
                      <li key={item.reference}>
                        <PieceListItem item={item} />
                      </li>
                    ))}
                  </ul>
                </PiecesSection>
              )}

              {cabinetPiecesList.others.length > 0 && (
                <PiecesSection>
                  <PiecesSectionTitle>{t('Others')}</PiecesSectionTitle>
                  <ul>
                    {cabinetPiecesList.others.map(item => (
                      <li key={item.reference}>
                        <PieceListItem item={item} />
                      </li>
                    ))}
                    {cabinetPiecesList.wheelsBases.map(item => (
                      <li key={item.reference}>
                        <PieceListItem item={item} />
                      </li>
                    ))}
                  </ul>
                </PiecesSection>
              )}
            </div>
          </div>
          <ReactTooltip id="colorInfo" place="bottom" type="dark" />
        </div>
      );
    }
  }
  return null;
}

export default PiecesList;

const PiecesSection = styled.div`
  display: flex;
  margin: 0.4rem 0;
`;

const PiecesSectionTitle = styled.div`
  font-size: 1.125rem;
  font-weight: 500;
  width: 5.3rem;
`;
